import React, { useRef } from "react";
import {
  ChevronRight,
  ChevronLeft,
  Heart,
  ShoppingCart,
  Star,
  ExternalLink,
} from "lucide-react";
import { useTranslation } from "react-i18next";

// Temporary mock data - will be replaced with real data later
const MOCK_DISCOVER_MORE = [
  {
    id: "1",
    name: "Basic Sateen Skirt",
    price: 89.9,
    rating: 4.5,
    reviews: 2890,
    platform: "Shopify.com",
    image:
      "https://images.unsplash.com/photo-1583496661160-fb5886a0aaaa?w=300&h=300&fit=crop",
    isFavorite: false,
  },
  {
    id: "2",
    name: "Nike Field General",
    price: 89.9,
    rating: 4.5,
    reviews: 2890,
    platform: "Amazon.com",
    image:
      "https://images.unsplash.com/photo-1542272604-787c3835535d?w=300&h=300&fit=crop",
    isFavorite: false,
  },
  {
    id: "3",
    name: "Canon Digital Camera",
    price: 89.9,
    rating: 4.5,
    reviews: 2890,
    platform: "Amazon.com",
    image:
      "https://images.unsplash.com/photo-1606472331962-4c61d6180d62?w=300&h=300&fit=crop",
    isFavorite: false,
  },
  {
    id: "4",
    name: "Basic Sateen Skirt",
    price: 39.9,
    originalPrice: 89.9,
    discount: 40,
    rating: 4.5,
    reviews: 2890,
    platform: "Amazon.com",
    image:
      "https://images.unsplash.com/photo-1600185365926-3a2ce3cdb9eb?w=300&h=300&fit=crop",
    isFavorite: false,
  },
  {
    id: "5",
    name: "Basic Sateen Skirt",
    price: 89.9,
    rating: 4.5,
    reviews: 2890,
    platform: "Amazon.com",
    image:
      "https://images.unsplash.com/photo-1583496661160-fb5886a0aaaa?w=300&h=300&fit=crop",
    isFavorite: false,
  },
];

export const DiscoverMoreSection: React.FC = () => {
  const { t } = useTranslation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: "smooth" });
    }
  };

  return (
    <div className="flex flex-col gap-3 md:gap-4">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-gray-100">
            {t("cart.discoverMoreProducts")}
          </h2>
          <ChevronRight className="h-5 w-5 md:h-6 md:w-6 text-gray-900 dark:text-gray-100 hidden md:block" />
        </div>
        {/* Mobile Navigation Buttons */}
        <div className="flex items-center gap-2 md:hidden">
          <button
            onClick={scrollLeft}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            <ChevronLeft className="h-4 w-4 text-gray-900 dark:text-gray-100" />
          </button>
          <button
            onClick={scrollRight}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            <ChevronRight className="h-4 w-4 text-gray-900 dark:text-gray-100" />
          </button>
        </div>
      </div>

      {/* Products Grid */}
      <div
        ref={scrollContainerRef}
        className="flex gap-2 md:gap-4 overflow-x-auto pb-2 scrollbar-hide"
      >
        {MOCK_DISCOVER_MORE.map((product) => (
          <div
            key={product.id}
            className="flex-shrink-0 border border-gray-200 dark:border-gray-700 rounded-xl md:rounded-2xl p-1.5 md:p-2"
          >
            {/* Product Image */}
            <div className="relative h-[160px] w-[160px] md:h-[224px] md:w-[224px] rounded-xl md:rounded-2xl overflow-hidden bg-gray-100 dark:bg-gray-800 mb-2 md:mb-3">
              <img
                src={product.image}
                alt={product.name}
                className="h-full w-full object-cover"
              />

              {/* Favorite Button */}
              <button className="absolute bottom-1.5 right-1.5 md:bottom-2 md:right-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full p-1.5 md:p-2 hover:bg-gray-50 dark:hover:bg-gray-700">
                <Heart
                  className={`h-3 w-3 md:h-3.5 md:w-3.5 ${
                    product.isFavorite
                      ? "fill-current text-blue-600 dark:text-blue-500"
                      : "text-gray-700 dark:text-gray-300"
                  }`}
                />
              </button>

              {/* Buy Now Badge */}
              <div className="absolute top-1.5 right-1.5 md:top-2 md:right-2 flex items-center gap-0.5 md:gap-1 px-1 md:px-1.5 py-0.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md">
                <ShoppingCart className="h-3 w-3 md:h-3.5 md:w-3.5 text-gray-700 dark:text-gray-300" />
                <span className="text-[10px] md:text-xs font-medium text-gray-700 dark:text-gray-300">
                  {t("cart.buyNow")}
                </span>
              </div>
            </div>

            {/* Product Info */}
            <div className="flex flex-col gap-1.5 md:gap-2 p-0.5 md:p-1">
              {/* Platform & Rating */}
              <div className="flex items-center justify-between gap-1">
                <div className="inline-flex items-center gap-0.5 md:gap-1 px-1 md:px-1.5 py-0.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md">
                  <span className="text-[10px] md:text-xs font-medium text-gray-700 dark:text-gray-300">
                    {product.platform}
                  </span>
                  <ExternalLink className="h-2.5 w-2.5 md:h-3 md:w-3 text-gray-700 dark:text-gray-300" />
                </div>
                <div className="flex items-center gap-0.5 md:gap-1">
                  <Star className="h-3 w-3 md:h-3.5 md:w-3.5 fill-current text-blue-600 dark:text-blue-500" />
                  <span className="text-[10px] md:text-xs font-medium text-gray-900 dark:text-gray-100">
                    {product.rating}
                  </span>
                  <span className="text-[10px] md:text-xs text-gray-500 dark:text-gray-400 hidden sm:inline">
                    ({product.reviews.toLocaleString()})
                  </span>
                </div>
              </div>

              {/* Product Name */}
              <h3 className="text-xs md:text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                {product.name}
              </h3>

              {/* Price */}
              <div className="flex items-center gap-1.5 md:gap-2 flex-wrap">
                {product.originalPrice ? (
                  <>
                    <span className="text-xs md:text-sm font-semibold text-blue-600 dark:text-blue-500">
                      €{product.price.toFixed(2)}
                    </span>
                    <span className="text-[10px] md:text-xs line-through text-gray-500 dark:text-gray-400">
                      €{product.originalPrice.toFixed(2)}
                    </span>
                    <span className="px-1 md:px-1.5 py-0.5 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded text-[10px] md:text-xs font-medium text-blue-600 dark:text-blue-400">
                      %{product.discount} off
                    </span>
                  </>
                ) : (
                  <span className="text-xs md:text-sm font-medium text-gray-900 dark:text-gray-100">
                    €{product.price.toFixed(2)}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

